import { NavLink, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Wallet,
  ArrowLeftRight,
  Tags,
  Repeat,
  CreditCard,
  TrendingUp,
  FileBarChart,
  FlaskConical,
  Target,
  Users,
  Settings,
  HelpCircle,
  X,
  Zap,
  Sparkles,
  Star,
  Gift,
  PanelLeftClose,
  PanelLeftOpen,
  Link2,
  PieChart,
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';

const navSections = [
  {
    label: null,
    items: [
      { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard },
      { name: 'Contas', href: '/app/accounts', icon: Wallet },
      { name: 'Transacoes', href: '/app/transactions', icon: ArrowLeftRight },
      { name: 'Categorias', href: '/app/categories', icon: Tags },
      { name: 'Sonhos e Metas', href: '/app/goals', icon: Target },
      { name: 'Orcamento', href: '/app/budget', icon: PieChart },
    ],
  },
  {
    label: 'Financeiro',
    items: [
      { name: 'Recorrencias', href: '/app/recurrences', icon: Repeat, feature: 'recurrences' },
      { name: 'Parcelamentos', href: '/app/installments', icon: CreditCard, feature: 'installments' },
      { name: 'Renda Fixa', href: '/app/investments', icon: TrendingUp, feature: 'investments' },
      { name: 'Open Finance', href: '/app/open-finance', icon: Link2, feature: 'openFinance' },
    ],
  },
  {
    label: 'Analise',
    items: [
      { name: 'Relatorios', href: '/app/reports', icon: FileBarChart, feature: 'reports' },
      { name: 'Simulacao', href: '/app/simulation', icon: FlaskConical, feature: 'simulation' },
      { name: 'Consultor IA', href: '/app/advisor', icon: Sparkles, feature: 'advisor' },
    ],
  },
  {
    label: 'Compartilhado',
    items: [
      { name: 'Familia', href: '/app/family', icon: Users, feature: 'family', badge: 'FAMILY' },
    ],
  },
  {
    label: null,
    items: [
      { name: 'Meu Nivel', href: '/app/gamification', icon: Star },
      { name: 'Indicar Amigos', href: '/app/referral', icon: Gift },
    ],
  },
];

const secondaryNavigation = [
  { name: 'Configuracoes', href: '/app/settings/profile', icon: Settings },
  { name: 'Ajuda', href: '/app/help', icon: HelpCircle },
];

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { hasFeature, tenant } = useAuth();

  const NavItem = ({ item }) => {
    if (item.feature && !hasFeature(item.feature)) {
      return (
        <div
          className={clsx(
            'flex items-center rounded-xl text-sm font-medium text-gray-400 dark:text-gray-600 cursor-not-allowed select-none',
            isCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
          )}
          title={isCollapsed ? item.name : undefined}
        >
          <item.icon size={18} className="flex-shrink-0" />
          {!isCollapsed && (
            <>
              <span className="flex-1">{item.name}</span>
              <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 px-1.5 py-0.5 rounded-full font-semibold tracking-wide">
                {item.badge || 'PRO'}
              </span>
            </>
          )}
        </div>
      );
    }

    return (
      <NavLink
        to={item.href}
        onClick={() => onClose?.()}
        title={isCollapsed ? item.name : undefined}
        className={({ isActive }) =>
          clsx(
            'flex items-center rounded-xl text-sm font-medium transition-all duration-150',
            isCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
            isActive
              ? 'bg-primary-600 text-white shadow-sm shadow-primary-500/30'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800/80'
          )
        }
      >
        {({ isActive }) => (
          <>
            <item.icon size={18} className="flex-shrink-0" />
            {!isCollapsed && <span>{item.name}</span>}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}

      <aside
        className={clsx(
          'fixed lg:static inset-y-0 left-0 z-50',
          'bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800',
          'transform transition-all duration-300 ease-in-out lg:transform-none',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          isCollapsed ? 'lg:w-[68px]' : 'w-64'
        )}
        style={isOpen ? { width: '16rem' } : undefined}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 border-b border-gray-100 dark:border-gray-800 px-3">
            <Link to="/app/dashboard" className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-sm shadow-primary-500/30">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              {!isCollapsed && (
                <span className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                  MyBank
                </span>
              )}
            </Link>
            {/* Mobile: close button */}
            <button
              onClick={onClose}
              className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={18} />
            </button>
            {/* Desktop: collapse/expand toggle */}
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={isCollapsed ? 'Expandir menu' : 'Minimizar menu'}
            >
              {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>

          {/* Nav */}
          <nav className={clsx(
            'flex-1 py-4 space-y-1 overflow-y-auto',
            isCollapsed ? 'px-2' : 'px-3'
          )}>
            {navSections.map((section, idx) => (
              <div key={idx}>
                {idx > 0 && (
                  <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800" />
                )}
                {section.label && !isCollapsed && (
                  <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-600">
                    {section.label}
                  </p>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavItem key={item.name} item={item} />
                  ))}
                </div>
              </div>
            ))}

            <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800 space-y-0.5">
              {secondaryNavigation.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </div>
          </nav>

          {/* Upgrade banner */}
          {tenant?.plan === 'free' && !isCollapsed && (
            <div className="p-3 mx-3 mb-3">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 to-primary-800 p-4 text-white shadow-lg shadow-primary-500/20">
                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10 blur-xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap size={14} className="text-yellow-300" />
                    <p className="text-sm font-semibold">Upgrade para Pro</p>
                  </div>
                  <p className="text-xs text-white/70 mb-3">
                    Desbloqueie todas as funcionalidades
                  </p>
                  <Link
                    to="/app/settings/billing"
                    className="block w-full py-1.5 bg-white text-primary-700 rounded-xl text-xs font-semibold text-center hover:bg-primary-50 transition-colors"
                  >
                    Ver planos
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Collapsed upgrade icon */}
          {tenant?.plan === 'free' && isCollapsed && (
            <div className="flex justify-center pb-3">
              <Link
                to="/app/settings/billing"
                className="p-2 rounded-xl bg-gradient-to-br from-primary-600 to-primary-800 text-yellow-300 shadow-sm hover:shadow-md transition-shadow"
                title="Upgrade para Pro"
              >
                <Zap size={16} />
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
